/**
 * Centralized date utility functions for frontend
 * Ensures consistent date handling and UK timezone enforcement
 * Works in conjunction with existing timeFormatting.ts
 */

/**
 * UK timezone identifier - all business logic uses UK time
 */
export const UK_TIMEZONE = 'Europe/London';

/**
 * Safely parse a date string or Date object to a Date
 * Handles various input formats and ensures validity
 */
export function parseDate(date: string | Date | null | undefined): Date | null {
  if (!date) return null;

  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date;
  }

  const parsed = new Date(date);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Create a date in UK timezone from components
 * @param year - Year
 * @param month - Month (1-12)
 * @param day - Day of month
 * @param hours - Hours (0-23)
 * @param minutes - Minutes (0-59)
 * @returns Date object representing UK time
 */
export function createUKDate(
  year: number,
  month: number,
  day: number,
  hours: number = 0,
  minutes: number = 0
): Date {
  // Create date using local date constructor to avoid timezone shifts
  return new Date(year, month - 1, day, hours, minutes, 0, 0);
}

/**
 * Create a date from a date string and time components
 * @param dateStr - Date in YYYY-MM-DD format
 * @param hours - Hours (0-23)
 * @param minutes - Minutes (0-59)
 * @returns Date object
 */
export function createDateFromComponents(
  dateStr: string,
  hours: number,
  minutes: number
): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return createUKDate(year, month, day, hours, minutes);
}

/**
 * Convert 12-hour time to 24-hour
 * @param hour - Hour (1-12)
 * @param minute - Minute (0-59)
 * @param period - AM or PM
 * @returns Object with 24-hour hours and minutes
 */
export function to24Hour(
  hour: number,
  minute: number,
  period: 'AM' | 'PM'
): { hours: number; minutes: number } {
  let hours24 = hour;
  if (period === 'AM' && hour === 12) {
    hours24 = 0;
  } else if (period === 'PM' && hour !== 12) {
    hours24 = hour + 12;
  }
  return { hours: hours24, minutes: minute };
}

/**
 * Convert 24-hour time to 12-hour
 * @param hours - Hours (0-23)
 * @param minutes - Minutes (0-59)
 * @returns Object with 12-hour time components
 */
export function to12Hour(
  hours: number,
  minutes: number
): { hour: number; minute: number; period: 'AM' | 'PM' } {
  const period = hours >= 12 ? 'PM' : 'AM';
  let hour12 = hours % 12;
  if (hour12 === 0) hour12 = 12;

  return {
    hour: hour12,
    minute: minutes,
    period
  };
}

/**
 * Format date for datetime-local input
 * @param date - Date to format
 * @returns String in YYYY-MM-DDTHH:mm format
 */
export function formatForDateTimeInput(date: Date | string | null): string {
  const d = parseDate(date);
  if (!d) return '';

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Format date for date input
 * @param date - Date to format
 * @returns String in YYYY-MM-DD format
 */
export function formatForDateInput(date: Date | string | null): string {
  const d = parseDate(date);
  if (!d) return '';

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Parse datetime-local input value to Date
 * @param value - Value from datetime-local input
 * @returns Date object or null
 */
export function parseDateTimeInput(value: string): Date | null {
  if (!value) return null;

  // datetime-local format: YYYY-MM-DDTHH:mm
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return null;

  const [, year, month, day, hours, minutes] = match;
  return createUKDate(
    parseInt(year),
    parseInt(month),
    parseInt(day),
    parseInt(hours),
    parseInt(minutes)
  );
}

/**
 * Add hours to a date (handles DST correctly)
 * @param date - Base date
 * @param hours - Hours to add (can be negative)
 * @returns New date
 */
export function addHours(date: Date | string, hours: number): Date {
  const d = parseDate(date);
  if (!d) return new Date();

  // Use millisecond arithmetic to handle DST correctly
  return new Date(d.getTime() + hours * 60 * 60 * 1000);
}

/**
 * Add days to a date
 * @param date - Base date
 * @param days - Days to add (can be negative)
 * @returns New date
 */
export function addDays(date: Date | string, days: number): Date {
  const d = parseDate(date);
  if (!d) return new Date();

  const result = new Date(d);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Add minutes to a date
 * @param date - Base date
 * @param minutes - Minutes to add (can be negative)
 * @returns New date
 */
export function addMinutes(date: Date | string, minutes: number): Date {
  const d = parseDate(date);
  if (!d) return new Date();

  return new Date(d.getTime() + minutes * 60 * 1000);
}

/**
 * Get start of day
 * @param date - Input date
 * @returns Date at 00:00:00
 */
export function getStartOfDay(date: Date | string): Date {
  const d = parseDate(date);
  if (!d) return new Date();

  const year = d.getFullYear();
  const month = d.getMonth();
  const day = d.getDate();
  return new Date(year, month, day, 0, 0, 0, 0);
}

/**
 * Get end of day
 * @param date - Input date
 * @returns Date at 23:59:59.999
 */
export function getEndOfDay(date: Date | string): Date {
  const d = parseDate(date);
  if (!d) return new Date();

  const year = d.getFullYear();
  const month = d.getMonth();
  const day = d.getDate();
  return new Date(year, month, day, 23, 59, 59, 999);
}

/**
 * Check if two dates are on the same day
 * @param date1 - First date
 * @param date2 - Second date
 * @returns True if same day
 */
export function isSameDay(date1: Date | string, date2: Date | string): boolean {
  const d1 = parseDate(date1);
  const d2 = parseDate(date2);

  if (!d1 || !d2) return false;

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

/**
 * Check if date is today
 * @param date - Date to check
 * @returns True if date is today
 */
export function isToday(date: Date | string): boolean {
  return isSameDay(date, new Date());
}

/**
 * Check if date is in the past
 * @param date - Date to check
 * @returns True if date is in the past
 */
export function isPastDate(date: Date | string): boolean {
  const d = parseDate(date);
  if (!d) return false;

  return d.getTime() < Date.now();
}

/**
 * Check if date is in the future
 * @param date - Date to check
 * @returns True if date is in the future
 */
export function isFutureDate(date: Date | string): boolean {
  const d = parseDate(date);
  if (!d) return false;

  return d.getTime() > Date.now();
}

/**
 * Calculate duration between two dates in hours
 * @param startTime - Start time
 * @param endTime - End time
 * @returns Duration in hours (decimal)
 */
export function calculateDurationHours(
  startTime: Date | string,
  endTime: Date | string
): number {
  const start = parseDate(startTime);
  const end = parseDate(endTime);

  if (!start || !end) return 0;

  const milliseconds = end.getTime() - start.getTime();
  return milliseconds / (1000 * 60 * 60);
}

/**
 * Calculate duration between two dates in minutes
 * @param startTime - Start time
 * @param endTime - End time
 * @returns Duration in minutes
 */
export function calculateDurationMinutes(
  startTime: Date | string,
  endTime: Date | string
): number {
  const start = parseDate(startTime);
  const end = parseDate(endTime);

  if (!start || !end) return 0;

  const milliseconds = end.getTime() - start.getTime();
  return Math.round(milliseconds / (1000 * 60));
}

/**
 * Get day of week (0=Sunday, 6=Saturday)
 * @param date - Input date
 * @returns Day of week number
 */
export function getDayOfWeek(date: Date | string): number {
  const d = parseDate(date);
  return d ? d.getDay() : 0;
}

/**
 * Get week difference between two dates
 * @param date1 - First date
 * @param date2 - Second date
 * @returns Number of weeks between dates
 */
export function getWeeksDifference(date1: Date | string, date2: Date | string): number {
  const d1 = parseDate(date1);
  const d2 = parseDate(date2);

  if (!d1 || !d2) return 0;

  const milliseconds = d2.getTime() - d1.getTime();
  return Math.round(milliseconds / (7 * 24 * 60 * 60 * 1000));
}

/**
 * Format date for API calls (ISO string)
 * @param date - Date to format
 * @returns ISO string or empty string
 */
export function formatForAPI(date: Date | string | null): string {
  const d = parseDate(date);
  return d ? d.toISOString() : '';
}

/**
 * Validate date string
 * @param dateStr - Date string to validate
 * @returns True if valid date
 */
export function isValidDate(dateStr: string): boolean {
  const date = parseDate(dateStr);
  return date !== null;
}

/**
 * Get minimum selectable date (today)
 * @returns Today's date formatted for date input
 */
export function getMinSelectableDate(): string {
  return formatForDateInput(new Date());
}

/**
 * Get maximum selectable date (e.g., 3 months from now)
 * @param monthsAhead - Number of months ahead (default 3)
 * @returns Future date formatted for date input
 */
export function getMaxSelectableDate(monthsAhead: number = 3): string {
  const future = new Date();
  future.setMonth(future.getMonth() + monthsAhead);
  return formatForDateInput(future);
}

/**
 * Create consistent time slot for bookings
 * Ensures no DST issues when creating booking times
 * @param date - Base date
 * @param startHour - Start hour
 * @param startMinute - Start minute
 * @param durationMinutes - Duration in minutes
 * @returns Object with start and end times
 */
export function createTimeSlot(
  date: Date | string,
  startHour: number,
  startMinute: number,
  durationMinutes: number
): { startTime: Date; endTime: Date } {
  const baseDate = parseDate(date);
  if (!baseDate) {
    return {
      startTime: new Date(),
      endTime: new Date()
    };
  }

  const year = baseDate.getFullYear();
  const month = baseDate.getMonth();
  const day = baseDate.getDate();

  const startTime = new Date(year, month, day, startHour, startMinute, 0, 0);
  const endTime = addMinutes(startTime, durationMinutes);

  return { startTime, endTime };
}